import { useCallback, type FormEvent } from 'react';
import type { RiskScoreWindow } from '@amzn/gbsa-helios-typescript-client';
import {
  ToggleButton,
  ToggleButtonGroup,
} from '@amzn/stencil-react-components/toggle-button';

import { useMessages } from 'src/v2/shared/i18n/useMessages';
import { buildTimeWindowOptions, decodeRiskScoreWindow } from '../logic/riskTimeWindow';
import { RISK_ENGINE_BUNDLE, type RiskEngineMessageKey } from '../translations';
import { RISK_ENGINE_TEST_IDS } from '../testIds';

interface RiskTimeWindowToggleProps {
  readonly value: RiskScoreWindow;
  readonly onChange: (window: RiskScoreWindow) => void;
}

/**
 * The trailing-day scoring-window selector (7/14/30/90) for the drill-down header. A compact
 * single-select segmented control — the same window options and default as the Risk Engine list
 * page's filter, shared via `buildTimeWindowOptions`/`decodeRiskScoreWindow`, so the two surfaces
 * stay in lockstep. Controlled: the caller owns the selected window (the breakdown page keeps it in
 * the URL).
 */
export function RiskTimeWindowToggle({
  value,
  onChange,
}: RiskTimeWindowToggleProps): JSX.Element {
  const { t } = useMessages<RiskEngineMessageKey>(RISK_ENGINE_BUNDLE);
  const options = buildTimeWindowOptions(t);

  const handleChange = useCallback(
    (_event: FormEvent<HTMLElement>, next: string | string[]): void => {
      onChange(decodeRiskScoreWindow(Array.isArray(next) ? next[0] : next));
    },
    [onChange],
  );

  return (
    <ToggleButtonGroup
      dataTestId={RISK_ENGINE_TEST_IDS.timeWindow}
      onChange={handleChange}
      value={value}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} aria-label={option.label} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
